import * as Yup from 'yup';
import { CalibrationStyle } from './enum';

const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ipV4Regex = /^(\d{1,3}\.){3}\d{1,3}$/;

const calibrationStyleValues = Object.values(CalibrationStyle);
const binSchema = Yup.object().shape({
  id: Yup.string().matches(uuidRegex, 'Bin ID must be a valid UUID').required('Bin ID is required'),
  name: Yup.string()
    .min(1, 'Bin name must be at least 1 characters')
    .max(100, 'Bin name must not exceed 100 characters')
    .required('Bin name is required'),
  binCap: Yup.number().min(1, 'Bin capacity must be at least 1 unit').required('Bin capacity is required'),
});

const feederSchema = Yup.object().shape({
  id: Yup.string().matches(uuidRegex, 'Feeder ID must be a valid UUID').required('Feeder ID is required'),
  capacity_in_grams: Yup.number().min(1, 'Capacity must be at least 1').required('Capacity in grams is required'),
  quantity: Yup.number().min(1, 'Quantity must be at least 1').required('Quantity is required'),
  feed_line_id: Yup.string()
    .matches(uuidRegex, 'feed_line_id must be a valid UUID')
    .required('feed_line_id is required'),
});

const userSchema = Yup.object().shape({
  user_id: Yup.string().matches(uuidRegex, 'User ID must be a valid UUID').required('User ID is required'),
  role_id: Yup.string()
    .nullable()
    .when('is_tier_only', {
      is: true,
      then: (schema) => schema.nullable(),
      otherwise: (schema) =>
        schema.matches(uuidRegex, 'Role ID must be a valid UUID').required('Please select a role'),
    }),
  is_tier_only: Yup.boolean().optional(),
});

const feedLineSchema = Yup.object().shape({
  id: Yup.string().matches(uuidRegex, 'Feed line ID must be a valid UUID').required('Feed line ID is required'),
  name: Yup.string().min(1, 'Feed line name must have at least 1 character').required('Feed line name is required'),
  bins: Yup.array()
    .of(Yup.string().matches(uuidRegex, 'Each bin must be a valid UUID').required('Bin ID is required'))
    .min(1, 'At least one bin is required')
    .required('Bins array is required'),
  feeders: Yup.array().of(feederSchema).required('Feeders array is required'),
});

const networkProfileSchema = Yup.object().shape({
  priority: Yup.number().integer().min(1, 'Priority must be at least 1').required('Priority is required'),
  wifiSsid: Yup.string().min(1, 'SSID is required').max(32, 'SSID must not exceed 32 characters').required('SSID is required'),
  wifiPassword: Yup.string().max(63, 'Password must not exceed 63 characters').optional(),
});

const binControllerSchema = Yup.object().shape({
  // Static IP support commented out until firmware supports it:
  // ipAssignment: Yup.string().oneOf(['dynamic', 'static']).optional(),
  // staticIpAddress: Yup.string().when('ipAssignment', {
  //   is: 'static',
  //   then: (s) => s.matches(ipV4Regex, 'Enter a valid IPv4 address').required('IP address is required for static mode'),
  //   otherwise: (s) => s.optional(),
  // }),
  recoveryTimeSeconds: Yup.number()
    .integer()
    .min(0, 'Must be 0 or greater')
    .max(65535, 'Must not exceed 65535')
    .nullable()
    .optional(),
  vibrationDelaySeconds: Yup.number()
    .integer()
    .min(0, 'Must be 0 or greater')
    .max(65535, 'Must not exceed 65535')
    .nullable()
    .optional(),
  vibrationDurationSeconds: Yup.number()
    .integer()
    .min(0, 'Must be 0 or greater')
    .max(65535, 'Must not exceed 65535')
    .nullable()
    .optional(),
  minimumFloPercent: Yup.number().min(0).max(100).nullable().optional(),
  shutterOpenPercent: Yup.number().min(0).max(100).nullable().optional(),
  shutterTimeToFullOpenSeconds: Yup.number()
    .integer()
    .min(0)
    .max(65535)
    .nullable()
    .optional(),
  shutterTimeToFullCloseSeconds: Yup.number()
    .integer()
    .min(0)
    .max(65535)
    .nullable()
    .optional(),
});

export const barnSettingsCheckSchema = Yup.object().shape({
  barnName: Yup.string().min(1, 'Barn Name Required').required('Barn name is required'),
  calibrationStyle: Yup.string().oneOf(calibrationStyleValues).required('Calibration Option Required'),
  users: Yup.array().of(userSchema).required('Users array is required'),
  bins: Yup.array()
    .of(binSchema) // Each bin must adhere to the `binSchema`
    .min(1, 'At least one bin is required')
    .required('Bins array is required'),
  feedLines: Yup.array()
    .of(feedLineSchema)
    .min(1, 'At least one feed line is required')
    .required('Feed lines array is required'),
  feedOrderShippingTimeInDays: Yup.number()
    .min(1, 'Shipping time must be at least 1 day')
    .max(30, 'Shipping time must not exceed 30 days')
    .required('Feed order shipping time is required'),
  typicalFeedDeliverySizeInTons: Yup.number()
    .min(0.1, 'Delivery size must be at least 0.1 tons')
    .max(100, 'Delivery size must not exceed 100 tons')
    .required('Typical feed delivery size is required'),
  networkProfiles: Yup.array().of(networkProfileSchema).optional(),
  binControllers: Yup.array().of(binControllerSchema).optional(),
});

export const feederCheckSchema = Yup.object().shape({
  valuesInSmallUnits: Yup.number().min(1, 'Capacity must be at least 1').required('Capacity is required'),
  quantity: Yup.number().min(1, 'Quantity must be at least 1').required('Quantity is required'),
});
